import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Brand + version are pulled from /settings/public so the site footer
  // (and any future About panel) stay in sync with what the admin picks
  // in Rental Settings → Company / Brand.
  // v4.2.4 — brand, support contact and the footer category columns are
  // all driven by settings / live fleet data. No hardcoded strings.
  const [brand, setBrand] = useState({ name: "", version: "", email: "", phone: "" });
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/settings/public")
      .then((r) => setBrand({
        name: r.data?.brand_name || r.data?.company_name || "",
        version: r.data?.app_version || "",
        email: r.data?.support_email || "",
        phone: r.data?.support_phone || "",
      }))
      .catch(() => {});
    api.get("/public/categories")
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCategories([]));
  }, []);

  const brandName = brand.name || "Brainart Rent";
  const catCols = [categories.slice(0, 5), categories.slice(5, 10)];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (p) => location.pathname === p;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F6F5F2" }}>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{
          background: "rgba(246, 245, 242, 0.78)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
        data-testid="site-header"
      >
        <div className="px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-2xl tracking-tight"
            data-testid="brand-logo"
          >
            {brandName}
          </Link>

          <nav className="hidden md:flex items-center gap-10 text-sm">
            <Link
              to="/manage-booking"
              className={`link-underline ${isActive("/manage-booking") ? "font-semibold" : ""}`}
              data-testid="nav-manage-booking"
            >
              Manage Booking
            </Link>
            {user && (
              <>
                <Link
                  to="/"
                  className={`link-underline ${isActive("/") ? "font-semibold" : ""}`}
                  data-testid="nav-home"
                >
                  Home
                </Link>
                <Link
                  to="/vehicles"
                  className={`link-underline ${isActive("/vehicles") ? "font-semibold" : ""}`}
                  data-testid="nav-vehicles"
                >
                  Fleet
                </Link>
              </>
            )}
            {user && (
              <Link
                to="/dashboard"
                className={`link-underline ${isActive("/dashboard") ? "font-semibold" : ""}`}
                data-testid="nav-dashboard"
              >
                My Bookings
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "super_admin") && (
              <Link
                to="/admin"
                className={`link-underline ${isActive("/admin") ? "font-semibold" : ""}`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
            {(user?.role === "staff" || user?.role === "editor") && (
              <Link
                to="/admin/bookings"
                className={`link-underline ${isActive("/admin/bookings") ? "font-semibold" : ""}`}
                data-testid="nav-staff"
              >
                Staff Console
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-sm text-neutral-600" data-testid="user-email">
                  {user.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm border border-black/20 hover:border-black px-5 py-2 transition-colors"
                  data-testid="logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm link-underline"
                  data-testid="login-link"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="text-sm bg-black text-white hover:bg-neutral-800 px-5 py-2 transition-colors"
                  data-testid="register-link"
                >
                  Join {brandName}
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-black/5 px-6 py-6 space-y-4 bg-[#F6F5F2]">
            <Link to="/" className="block" onClick={() => setOpen(false)}>Home</Link>
            <Link to="/vehicles" className="block" onClick={() => setOpen(false)}>Fleet</Link>
            {user && (
              <Link to="/dashboard" className="block" onClick={() => setOpen(false)}>My Bookings</Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="block" onClick={() => setOpen(false)}>Admin</Link>
            )}
            <div className="divider-line my-3"></div>
            {user ? (
              <button onClick={handleLogout} className="block">Logout</button>
            ) : (
              <>
                <Link to="/login" className="block" onClick={() => setOpen(false)}>Sign in</Link>
                <Link to="/register" className="block" onClick={() => setOpen(false)}>Register</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer
        className="px-6 md:px-12 lg:px-20 py-16 border-t border-black/5 mt-20"
        style={{ background: "#EFECE6" }}
        data-testid="site-footer"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <p className="font-display text-3xl" data-testid="footer-brand">
              {brandName}
            </p>
            <p className="mt-4 text-sm text-neutral-600 max-w-md leading-relaxed">
              A curated fleet of remarkable cars — from urban runabouts to chauffeur-grade
              flagships. Booked in minutes, delivered to your door.
            </p>
          </div>
          {catCols.map((col, i) => (
            col.length > 0 && (
              <div key={i} data-testid={`footer-explore-col-${i}`}>
                <p className="overline mb-4">{i === 0 ? "Explore" : "Categories"}</p>
                <ul className="space-y-2 text-sm">
                  {i === 0 && (
                    <li><Link to="/vehicles" className="link-underline">Fleet</Link></li>
                  )}
                  {col.map((c) => (
                    <li key={c.category}>
                      <Link to={`/vehicles?category=${encodeURIComponent(c.category)}`} className="link-underline">
                        {c.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ))}
          <div>
            <p className="overline mb-4">Contact</p>
            <ul className="space-y-2 text-sm text-neutral-600">
              <li data-testid="footer-support-email">{brand.email || "hello@brainart.gr"}</li>
              <li data-testid="footer-support-phone">{brand.phone || "+30 210 000 0000"}</li>
              <li>24/7 Concierge</li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-black/5 text-xs text-neutral-500 flex flex-col md:flex-row md:justify-between gap-2">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} {brandName}. All rights reserved.
          </p>
          <p data-testid="footer-tagline">
            Crafted with precision by Brainart Creative Technologies{brand.version ? ` · ${brand.version}` : ""}
          </p>
        </div>
      </footer>
    </div>
  );
}
